import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { FileText, AlertCircle, MoreVertical, Edit, Trash, Copy } from 'lucide-react';
import { Task } from '../../types/task';
import { PriorityBadge } from './PriorityBadge';
import { Checkbox } from '../ui/Checkbox';
interface TaskRowProps {
  task: Task;
  onComplete: (taskId: string) => void;
}
export const TaskRow = ({
  task,
  onComplete
}: TaskRowProps) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const handleCheckboxChange = () => {
    setIsConfirming(true);
  };
  const handleConfirmComplete = () => {
    onComplete(task.id);
    setIsConfirming(false);
  };
  const handleCancelComplete = () => {
    setIsConfirming(false);
  };
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };
  return <div className="border-b border-border group" data-id="element-137">
      {/* Main row */}
      <div className="flex items-center py-3 px-4 hover:bg-surface-50 transition-colors duration-150" onClick={toggleExpanded} data-id="element-138">
        <div className="flex-shrink-0 mr-4" onClick={e => e.stopPropagation()} data-id="element-139">
          <Checkbox id={`task-${task.id}`} onChange={handleCheckboxChange} disabled={isConfirming} className="cursor-pointer" data-id="element-140" />
        </div>
        <div className="flex-shrink-0 mr-4 text-text-tertiary" data-id="element-141">
          <FileText size={20} data-id="element-142" />
        </div>
        <div className="min-w-0 flex-1" data-id="element-143">
          <div className="flex items-center" data-id="element-144">
            <p className="text-sm font-medium text-text-primary truncate" data-id="element-145">
              {task.fileName}
            </p>
            {task.priority === 'high' && <AlertCircle size={16} className="ml-2 text-error-500 flex-shrink-0" data-id="element-146" />}
          </div>
          <p className="text-xs text-text-secondary mt-1" data-id="element-147">
            Posted {formatDistanceToNow(new Date(task.postedAt))} ago by{' '}
            {task.postedBy.name}
          </p>
        </div>
        <div className="ml-2 md:ml-4 flex items-center" data-id="element-148">
          <div className="hidden sm:block" data-id="element-149">
            <PriorityBadge priority={task.priority} data-id="element-150" />
          </div>
          <div className="relative ml-2" onClick={e => e.stopPropagation()} data-id="element-151">
            <button className="p-1.5 rounded-full text-text-tertiary hover:bg-surface-200 hover:text-text-primary" onClick={() => setShowActions(!showActions)} aria-label="More actions" data-id="element-152">
              <MoreVertical size={16} data-id="element-153" />
            </button>
            {showActions && <div className="absolute right-0 mt-1 w-48 rounded-lg bg-surface-50 shadow-medium border border-border z-10 overflow-hidden" data-id="element-154">
                <div className="py-1" data-id="element-155">
                  <button className="flex w-full items-center px-4 py-2 text-sm text-text-primary hover:bg-surface-100" onClick={() => setShowActions(false)} data-id="element-156">
                    <Edit className="mr-2 h-4 w-4" data-id="element-157" />
                    Edit task
                  </button>
                  <button className="flex w-full items-center px-4 py-2 text-sm text-text-primary hover:bg-surface-100" onClick={() => setShowActions(false)} data-id="element-158">
                    <Copy className="mr-2 h-4 w-4" data-id="element-159" />
                    Duplicate
                  </button>
                  <button className="flex w-full items-center px-4 py-2 text-sm text-error-600 hover:bg-surface-100" onClick={() => setShowActions(false)} data-id="element-160">
                    <Trash className="mr-2 h-4 w-4" data-id="element-161" />
                    Delete
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </div>
      {/* Expanded details - only visible on mobile */}
      {isExpanded && <div className="px-4 pb-3 sm:hidden bg-surface-50 border-t border-border-200" data-id="element-162">
          <div className="flex items-center justify-between py-2" data-id="element-163">
            <span className="text-xs font-medium text-text-secondary" data-id="element-164">
              Priority:
            </span>
            <PriorityBadge priority={task.priority} data-id="element-165" />
          </div>
          {task.notes && <div className="mt-2" data-id="element-166">
              <span className="text-xs font-medium text-text-secondary" data-id="element-167">
                Notes:
              </span>
              <p className="text-sm text-text-primary mt-1" data-id="element-168">{task.notes}</p>
            </div>}
        </div>}
      {/* Confirmation modal */}
      {isConfirming && <div className="fixed inset-0 bg-bg-900 bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-169">
          <div className="bg-surface-50 p-5 rounded-modal shadow-strong max-w-md w-full" data-id="element-170">
            <h3 className="text-lg font-medium text-text-primary" data-id="element-171">
              Mark task as completed?
            </h3>
            <p className="mt-2 text-sm text-text-secondary" data-id="element-172">
              Mark "{task.fileName}" as completed? This moves it to your
              history.
            </p>
            <div className="mt-4 flex justify-end space-x-3" data-id="element-173">
              <button onClick={handleCancelComplete} className="px-4 py-2 text-sm font-medium text-text-primary bg-surface-100 rounded-input hover:bg-surface-200 transition-colors" data-id="element-174">
                Cancel
              </button>
              <button onClick={handleConfirmComplete} className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-input hover:bg-brand-700 transition-colors" data-id="element-175">
                Complete
              </button>
            </div>
          </div>
        </div>}
    </div>;
};